import { useEffect, useState, useMemo } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { Calculator, Plus, Trash2, Save, Zap, Moon, Calendar, MapPin, UserPlus, Ambulance, Wind, Baby, Stethoscope, UserCheck, Repeat, Clock, Banknote, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";
import { FORM_HELP } from "@/lib/helpTexts";

// Quick chips: conceptos de uso frecuente con icono + categoría.
// Si el código existe en la lista activa o en cualquier lista, usamos su precio; si no, fallback hardcoded.
const QUICK_CHIPS = [
    // SERVICIOS BASE (atención prehospitalaria)
    { code: "TBL",  label: "Básica · BLS",        sublabel: "Traslado simple",     icon: Ambulance,  group: "base",  fallback: 1800,  color: "emerald" },
    { code: "UBL",  label: "Urgencia BLS",         sublabel: "Soporte vital básico", icon: Ambulance,  group: "base",  fallback: 2200,  color: "amber" },
    { code: "TIM",  label: "Intermedia",           sublabel: "Soporte intermedio",   icon: Ambulance,  group: "base",  fallback: 3200,  color: "amber" },
    { code: "TIN",  label: "Intensiva",            sublabel: "Soporte avanzado ALS", icon: Ambulance,  group: "base",  fallback: 4800,  color: "red" },
    { code: "TFO",  label: "Foráneo",              sublabel: "Fuera de zona",        icon: Truck,      group: "base",  fallback: 3500,  color: "indigo" },
    { code: "THH",  label: "Hospital→Hospital",    sublabel: "Inter-hospitalario",   icon: Ambulance,  group: "base",  fallback: 2800,  color: "sky" },
    // EXTRAS CLÍNICOS (add-ons)
    { code: "OXI",  label: "Oxígeno",              sublabel: "Equipo + insumo",      icon: Wind,       group: "extra", fallback: 350,   color: "sky" },
    { code: "TPR",  label: "Incubadora",           sublabel: "Neonato/pediátrico",   icon: Baby,       group: "extra", fallback: 1500,  color: "pink" },
    { code: "MED",  label: "Médico (responsiva)",  sublabel: "Médico a bordo",       icon: Stethoscope,group: "extra", fallback: 1200,  color: "indigo" },
    { code: "MED_ESP", label: "Médico especialista", sublabel: "Anestesia / pediatra", icon: UserCheck,group: "extra", fallback: 2500,  color: "purple" },
    // OPERACIÓN
    { code: "KM_FOR", label: "Km foráneo",         sublabel: "Por kilómetro",        icon: Truck,      group: "ops",   fallback: 90,    color: "slate" },
    { code: "ESPERA", label: "Espera 30 min",      sublabel: "Bloque de espera",     icon: Clock,      group: "ops",   fallback: 450,   color: "slate" },
];

export default function Quoter() {
    const [serviceTypes, setServiceTypes] = useState([]);
    const [clients, setClients] = useState([]);
    const [priceLists, setPriceLists] = useState([]);
    const [priceListId, setPriceListId] = useState("");
    const [client, setClient] = useState("");
    const [items, setItems] = useState([]);
    const [discountMode, setDiscountMode] = useState("pct");
    const [discount, setDiscount] = useState(0);
    const [cashNoInvoice, setCashNoInvoice] = useState(false);
    const [waitHours, setWaitHours] = useState(0);
    const [tolls, setTolls] = useState(0);
    const [extras, setExtras] = useState([]);   // [{label, amount}]
    const [lateCancellation, setLateCancellation] = useState(false);
    const [failedDeparture, setFailedDeparture] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewParams, setPreviewParams] = useState({ service_type_code: "TBL", is_foreign: false, km: 0, hours: 0 });

    // ===== Fase 1: Tipo de cotización + Modo Auditor =====
    const [quoteKind, setQuoteKind] = useState("traslado"); // 'traslado' | 'evento' | 'guardia' | 'mixto'
    const [auditMode, setAuditMode] = useState(true);
    const [discountReason, setDiscountReason] = useState("");
    const [discountApproval, setDiscountApproval] = useState(null); // {by_name, at, reason} si se autorizó

    // Datos contextuales por tipo de cotización
    const [traslado, setTraslado] = useState({
        is_foreign: false,
        km: 0,
        is_round_trip: false,
        trips: 1,
        wait_minutes: 0,
        patient: "",
        origin: "",
        destination: "",
        service_date: "",
        notes: "",
    });
    const [evento, setEvento] = useState({
        event_name: "",
        location: "",
        date: "",
        start_time: "",
        end_time: "",
        responsible: "",
        responsible_phone: "",
        lines: [], // [{type:'ambulancia'|'paramedico', qty, hours, unit_price, notes}]
        notes: "",
    });
    const [guardia, setGuardia] = useState({
        modality: "12h", // '12h' | '24h' | 'mensual_12h' | 'mensual_24h'
        ambulances: 1,
        start_date: "",
        end_date: "",
        location: "",
        responsible: "",
        notes: "",
    });

    // Datos de prospecto (cuando NO hay cliente registrado)
    const [prospect, setProspect] = useState({
        name: "",
        phone: "",
        email: "",
        company: "",
        rfc: "",
    });
    const [showProspect, setShowProspect] = useState(false);

    const isAdmin = false; // (placeholder no usado en este componente; el role-based gate está al guardar)

    useEffect(() => {
        (async () => {
            try {
                const [t, c, pl] = await Promise.all([
                    api.get("/service-types"),
                    api.get("/clients"),
                    api.get("/price-lists"),
                ]);
                if (t?.data) setServiceTypes(t.data);
                if (c?.data) setClients(c.data);
                if (pl?.data) {
                    setPriceLists(pl.data);
                    const def = pl.data.find((p) => p.is_default) || pl.data[0];
                    if (def) setPriceListId(def.id);
                }
            } catch (e) { console.warn(e); }
        })();
    }, []);

    // Live pricing preview
    useEffect(() => {
        (async () => {
            try {
                const params = { ...previewParams };
                if (client) params.client_id = client;
                if (priceListId) params.price_list_id = priceListId;
                if (lateCancellation) params.late_cancellation = true;
                if (failedDeparture) params.failed_departure = true;
                const r = await api.get("/pricing/preview", { params });
                if (r?.data) setPreview(r.data);
            } catch (e) { console.warn(e); }
        })();
    }, [client, priceListId, previewParams, lateCancellation, failedDeparture]);

    // Auto-aplicar la lista de precios asignada al cliente seleccionado
    // (si el cliente tiene `price_list_id` configurado, el Cotizador la usa por defecto;
    // el usuario puede sobrescribirlo manualmente después).
    useEffect(() => {
        if (!client || !clients.length || !priceLists.length) return;
        const c = clients.find((x) => x.id === client);
        if (c?.price_list_id && priceLists.some((pl) => pl.id === c.price_list_id) && c.price_list_id !== priceListId) {
            // setTimeout para evitar warning de set-state-in-effect — no-op funcional
            setTimeout(() => setPriceListId(c.price_list_id), 0);
        }
    }, [client, clients, priceLists, priceListId]);

    // Build merged list of service types: global SERVICE_TYPES + custom from selected price list
    const activePriceList = useMemo(
        () => priceLists.find((p) => p.id === priceListId),
        [priceLists, priceListId]
    );
    const mergedServiceTypes = useMemo(() => {
        const baseList = serviceTypes.map((s) => ({ ...s, source: "catalog" }));
        const customList = (activePriceList?.custom_service_types || []).map((s) => ({
            code: s.code,
            name: s.name,
            base_price: s.base_price || 0,
            per_hour: s.hour_rate || 0,
            per_km: s.per_km || 0,
            round_trip_price: s.round_trip_price || null,
            notes: s.notes || "",
            source: "price_list",
        }));
        // Si hay un código duplicado, la versión de la lista pisa la del catálogo
        const map = new Map();
        baseList.forEach((s) => map.set(s.code, s));
        customList.forEach((s) => map.set(s.code, s));
        return Array.from(map.values());
    }, [serviceTypes, activePriceList]);

    const addItem = (code) => {
        const st = mergedServiceTypes.find((s) => s.code === code);
        if (!st) return;
        const isHourly = !!(st.per_hour && st.per_hour > 0);
        const baseRate = preview && preview.client_rate && st.source !== "price_list"
            ? preview.client_rate
            : st.base_price;
        setItems([...items, {
            code: st.code,
            description: st.name,
            qty: 1,
            hours: isHourly ? 1 : 0,
            hourly_rate: isHourly ? (st.per_hour || 0) : 0,
            is_hourly: isHourly,
            unit_price: isHourly ? (st.base_price + (st.per_hour || 0)) : baseRate,
            total: isHourly ? (st.base_price + (st.per_hour || 0)) : baseRate,
        }]);
    };

    // Resolve un precio con fallback en cascada: lista activa → cualquier lista cargada → fallback hardcoded.
    // Permite que las "Quick Chips" NUNCA fallen aunque la lista no esté completa.
    const resolvePrice = (code, fallback = 0) => {
        const it = mergedServiceTypes.find((s) => s.code === code);
        if (it?.base_price) return it.base_price;
        for (const pl of priceLists) {
            const m = (pl.custom_service_types || []).find((s) => s.code === code);
            if (m?.base_price) return m.base_price;
        }
        return fallback;
    };

    // Agrega un chip (concepto rápido) al carrito; respeta is_hourly si la lista lo marca.
    const addQuickChip = (chip) => {
        const it = mergedServiceTypes.find((s) => s.code === chip.code);
        const price = resolvePrice(chip.code, chip.fallback);
        const isHourly = !!(it?.per_hour && it.per_hour > 0);
        setItems([...items, {
            code: chip.code,
            description: it?.name || chip.label,
            qty: 1,
            hours: isHourly ? 1 : 0,
            hourly_rate: isHourly ? (it?.per_hour || 0) : 0,
            is_hourly: isHourly,
            unit_price: isHourly ? (price + (it?.per_hour || 0)) : price,
            total: isHourly ? (price + (it?.per_hour || 0)) : price,
        }]);
        toast.success(`${chip.label} agregado · ${money(price)}`);
    };

    const addManualItem = () => {
        setItems([...items, {
            code: "MAN",
            description: "",
            qty: 1,
            hours: 0,
            hourly_rate: 0,
            is_hourly: false,
            is_manual: true,
            unit_price: 0,
            total: 0,
        }]);
    };

    const addCustomConcept = (concept) => {
        // Para conceptos % de base: aplica al subtotal actual; los demás van como línea fija
        const base = subtotal || 0;
        let unitPrice = 0;
        let description = concept.label;
        if (concept.calculation === "percent_of_base") {
            unitPrice = +(base * (Number(concept.amount) || 0) / 100).toFixed(2);
            description = `${concept.label}${base ? ` (sobre ${base.toLocaleString("es-MX", { style: "currency", currency: "MXN" })})` : ""}`;
        } else {
            unitPrice = Number(concept.amount) || 0;
        }
        setItems([...items, {
            code: "CONV",
            description,
            qty: 1,
            hours: 0,
            hourly_rate: 0,
            is_hourly: false,
            is_manual: true,
            unit_price: unitPrice,
            total: unitPrice,
        }]);
    };

    const updateItem = (i, k, v) => {
        const copy = [...items];
        copy[i] = { ...copy[i], [k]: v };
        const it = copy[i];
        if (it.is_hourly) {
            const baseFix = serviceTypes.find((s) => s.code === it.code)?.base_price || 0;
            const hours = parseFloat(it.hours) || 0;
            const hr = parseFloat(it.hourly_rate) || 0;
            it.unit_price = baseFix + hours * hr;
            it.total = (parseFloat(it.qty) || 1) * it.unit_price;
        } else if (k === "qty" || k === "unit_price") {
            it.total = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
        }
        copy[i] = it;
        setItems(copy);
    };

    const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

    const subtotalItems = items.reduce((s, i) => s + (i.total || 0), 0);
    const eventoLinesTotal = (evento.lines || []).reduce((s, l) => s + ((l.qty || 0) * (l.hours || 0) * (l.unit_price || 0)), 0);

    // === FASE 2: Reglas de precio por tarifario (espera por bloques, km ida-vuelta, descuento máx) ===
    const pricingRules = activePriceList?.pricing_rules || {};
    const waitBlockMin = pricingRules.wait_block_minutes || 30;
    const waitGraceMin = pricingRules.wait_grace_minutes || 0;
    const waitRoundUp = pricingRules.wait_round_up !== false;
    const foreignKmDoubles = !!pricingRules.foreign_km_doubles_on_round_trip;
    const discountMaxPct = pricingRules.discount_max_pct_without_approval ?? 20;
    // Tarifa de espera (busca en custom_service_types primero, luego fallback)
    const waitItem = mergedServiceTypes.find((s) => s.code === "ESPERA" || s.subtype === "espera_30_min");
    const waitUnitPrice = waitItem?.base_price ?? 450;

    // Cargo de espera del traslado (FASE 2: por bloques con gracia)
    const trasladoWaitMin = traslado.wait_minutes || 0;
    const effectiveWaitMin = Math.max(0, trasladoWaitMin - waitGraceMin);
    const waitBlocks = waitRoundUp
        ? Math.ceil(effectiveWaitMin / waitBlockMin)
        : Math.floor(effectiveWaitMin / waitBlockMin);
    const trasladoWaitCharge = waitBlocks * waitUnitPrice;

    // Cargo de km foráneo (FASE 2: duplicar en ida-y-vuelta si la regla del tarifario lo dice)
    const kmItem = mergedServiceTypes.find((s) => s.code === "KM_FOR" || s.subtype === "km_foraneo");
    const kmUnitPrice = kmItem?.base_price ?? 90;
    const effectiveKm = (traslado.is_foreign && traslado.km > 0)
        ? (traslado.is_round_trip && foreignKmDoubles ? traslado.km * 2 : traslado.km)
        : 0;
    const trasladoForeignCharge = effectiveKm * kmUnitPrice;

    // Penalty pricing OVERRIDE: si está cancelación/salida fallida activa, usa el preview del backend (40%/60% de la tarifa base)
    const penaltyActive = !!(lateCancellation || failedDeparture);
    const penaltyFee = penaltyActive && preview ? (preview.late_cancellation_fee || preview.failed_departure_fee || 0) : 0;
    const waitCharge = penaltyActive ? 0 : ((parseFloat(waitHours) || 0) * 350 + trasladoWaitCharge);
    const tollsAmount = penaltyActive ? 0 : (parseFloat(tolls) || 0);
    const extrasTotal = penaltyActive ? 0 : extras.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const subtotal = penaltyActive ? penaltyFee : (subtotalItems + waitCharge + tollsAmount + extrasTotal + eventoLinesTotal + trasladoForeignCharge);
    const discountAmt = penaltyActive ? 0 : (discountMode === "amount"
        ? Math.min(parseFloat(discount) || 0, subtotal)
        : subtotal * ((parseFloat(discount) || 0) / 100));
    const taxable = Math.max(subtotal - discountAmt, 0);
    const iva = cashNoInvoice ? 0 : taxable * 0.16;
    const total = taxable + iva;

    // ===== MODO AUDITOR: validaciones que bloquean PDF / save =====
    const auditErrors = [];
    const auditWarnings = [];
    if (!client && !["traslado"].includes(quoteKind)) {
        auditWarnings.push("Cotización sin cliente asignado (Particular)");
    }
    if (!priceListId) auditErrors.push("Cotización sin tarifario asignado");
    if (activePriceList?.end_date) {
        try {
            if (new Date(activePriceList.end_date) < new Date()) auditErrors.push(`Tarifario "${activePriceList.name}" está vencido`);
        } catch (_e) { /* ignore */ }
    }
    if (quoteKind === "traslado" || quoteKind === "mixto") {
        if (items.length === 0 && eventoLinesTotal === 0) auditErrors.push("Traslado sin servicio base seleccionado");
        if (traslado.is_foreign && (!traslado.km || traslado.km <= 0)) auditErrors.push("Foráneo activo pero sin kilómetros capturados");
    }
    if (quoteKind === "evento" || quoteKind === "mixto") {
        if (!evento.event_name) auditErrors.push("Evento sin nombre");
        if (!evento.location) auditErrors.push("Evento sin lugar");
        const validLines = (evento.lines || []).filter((l) => (l.qty > 0 && l.hours > 0));
        if ((evento.lines || []).length === 0) auditErrors.push("Evento sin líneas (agrega al menos 1 ambulancia o paramédico)");
        else if (validLines.length === 0) auditErrors.push("Líneas del evento con 0 cantidad u horas");
    }
    if (quoteKind === "guardia" || quoteKind === "mixto") {
        if (!guardia.modality) auditErrors.push("Guardia sin modalidad seleccionada");
        if (quoteKind === "guardia" && (!guardia.ambulances || guardia.ambulances <= 0)) auditErrors.push("Guardia con 0 ambulancias");
    }
    // FASE 2: bloqueo descuento por % máximo configurable en el tarifario
    const discountPct = discountMode === "pct" ? (parseFloat(discount) || 0) : (subtotal > 0 ? (discountAmt / subtotal) * 100 : 0);
    if (discountAmt > 0 && discountPct > discountMaxPct && !discountApproval) {
        auditErrors.push(`Descuento ${discountPct.toFixed(1)}% supera el máximo permitido (${discountMaxPct}%) sin autorización especial`);
    }
    if (discountAmt > 0 && !discountReason) {
        auditWarnings.push("Descuento aplicado sin motivo capturado");
    }
    const auditBlocked = auditMode && auditErrors.length > 0;

    // ===== Fase 3: Comparador de tarifarios (Público vs Cliente) =====
    const [showCompare, setShowCompare] = useState(false);
    const publicPriceList = useMemo(
        () => priceLists.find((p) => p.is_default),
        [priceLists]
    );
    const compareEnabled = !!(activePriceList && publicPriceList && activePriceList.id !== publicPriceList.id);
    // Builds price map of {code → base_price} for a list
    const priceMapFor = (list) => {
        const m = {};
        (list?.custom_service_types || []).forEach((s) => {
            if (s?.code) m[s.code] = Number(s.base_price) || 0;
        });
        return m;
    };
    const publicMap = useMemo(() => priceMapFor(publicPriceList), [publicPriceList]);
    const activeMap = useMemo(() => priceMapFor(activePriceList), [activePriceList]);

    // Construye la comparación por item del carrito + totales (subtotal, IVA, total) en ambos tarifarios
    const compareRows = useMemo(() => {
        if (!compareEnabled) return [];
        return items.map((it, idx) => {
            const pubPrice = publicMap[it.code];
            const cliPrice = activeMap[it.code];
            // Para items hourly o manuales no hay equivalente directo → preservar precio actual
            const pubUnit = (typeof pubPrice === "number" && !it.is_manual && !it.is_hourly) ? pubPrice : it.unit_price;
            const cliUnit = (typeof cliPrice === "number" && !it.is_manual && !it.is_hourly) ? cliPrice : it.unit_price;
            const qty = parseFloat(it.qty) || 1;
            return {
                idx,
                code: it.code,
                description: it.description,
                qty,
                public_unit: pubUnit,
                client_unit: cliUnit,
                public_total: pubUnit * qty,
                client_total: cliUnit * qty,
                has_match: typeof pubPrice === "number",
            };
        });
    }, [items, compareEnabled, publicMap, activeMap]);

    const compareSubtotalPublic = compareRows.reduce((s, r) => s + (r.public_total || 0), 0) + waitCharge + tollsAmount + extrasTotal + eventoLinesTotal + trasladoForeignCharge;
    const compareSubtotalClient = compareRows.reduce((s, r) => s + (r.client_total || 0), 0) + waitCharge + tollsAmount + extrasTotal + eventoLinesTotal + trasladoForeignCharge;
    const compareTotalPublic = compareSubtotalPublic * 1.16;
    const compareTotalClient = compareSubtotalClient * 1.16;
    const compareDelta = compareTotalClient - compareTotalPublic;
    const compareDeltaPct = compareTotalPublic > 0 ? (compareDelta / compareTotalPublic) * 100 : 0;

    const save = async (sendAfter = null) => {
        if (auditMode && auditErrors.length > 0) {
            toast.error("Resuelve los errores del auditor antes de guardar: " + auditErrors[0]);
            return;
        }
        if (items.length === 0 && eventoLinesTotal === 0) return toast.error("Agrega al menos un concepto o línea de evento");
        // Convertir líneas del evento en items para que viajen en el payload
        const eventoItems = (evento.lines || []).filter((l) => l.qty > 0 && l.hours > 0).map((l) => ({
            code: l.type === "ambulancia" ? "EVT_AMB" : "EVT_PIE",
            description: `${l.label} · ${l.qty} × ${l.hours}h${l.notes ? ` (${l.notes})` : ""}`,
            qty: Number(l.qty) || 0,
            hours: Number(l.hours) || 0,
            hourly_rate: Number(l.unit_price) || 0,
            is_hourly: true,
            unit_price: Number(l.unit_price) || 0,
            total: (Number(l.qty) || 0) * (Number(l.hours) || 0) * (Number(l.unit_price) || 0),
        }));
        // Normalizar TODOS los items para que qty/unit_price/total/hours sean numéricos
        // (evita ValueError en backend PDF al formatear strings con :g/:.2f).
        const sanitize = (it) => ({
            ...it,
            qty: Number(it.qty) || 0,
            hours: Number(it.hours) || 0,
            hourly_rate: Number(it.hourly_rate) || 0,
            unit_price: Number(it.unit_price) || 0,
            total: Number(it.total) || 0,
        });
        const allItems = [...items.map(sanitize), ...eventoItems];
        try {
            const payload = {
                client_id: client || null,
                price_list_id: priceListId || null,
                quote_kind: quoteKind,
                service_type_code: allItems[0]?.code || "MAN",
                items: allItems,
                // Datos de prospecto (sólo si no hay cliente)
                prospect_name: !client ? (prospect.name || null) : null,
                prospect_phone: !client ? (prospect.phone || null) : null,
                prospect_email: !client ? (prospect.email || null) : null,
                prospect_company: !client ? (prospect.company || null) : null,
                prospect_rfc: !client ? (prospect.rfc || null) : null,
                traslado_meta: (quoteKind === "traslado" || quoteKind === "mixto") ? traslado : null,
                evento_meta: (quoteKind === "evento" || quoteKind === "mixto") ? { ...evento, lines: undefined } : null,
                guardia_meta: (quoteKind === "guardia" || quoteKind === "mixto") ? guardia : null,
                subtotal,
                wait_hours: parseFloat(waitHours) || 0,
                tolls: tollsAmount,
                extras,
                discount: discountAmt,
                discount_mode: discountMode,
                discount_value: parseFloat(discount) || 0,
                discount_reason: discountReason,
                discount_approval: discountApproval,
                iva,
                cash_no_invoice: cashNoInvoice,
                payment_method: cashNoInvoice ? "efectivo" : "factura",
                total,
                late_cancellation: lateCancellation,
                failed_departure: failedDeparture,
                // Snapshot del tarifario (Fase 1 — soporte básico)
                price_list_snapshot: activePriceList ? {
                    id: activePriceList.id,
                    name: activePriceList.name,
                    code: activePriceList.code,
                    end_date: activePriceList.end_date || null,
                    captured_at: new Date().toISOString(),
                } : null,
            };
            const { data } = await api.post("/quotes", payload);
            toast.success(`Cotización ${data.folio} guardada`);
            // Si el usuario eligió enviar al guardar, dispara el envío
            if (sendAfter) {
                try {
                    const sendResp = await api.post(`/quotes/${data.id}/send`, {
                        channels: sendAfter, // ['whatsapp'] | ['email'] | ['whatsapp','email']
                    });
                    if (sendResp.data?.mocked) {
                        toast.warning("Envío simulado (configura WhatsApp/Email en Integraciones para envío real).");
                    } else {
                        toast.success(`Cotización enviada por ${sendAfter.join(', ')}`);
                    }
                } catch (sendErr) {
                    toast.error("Cotización guardada pero falló el envío: " + formatApiError(sendErr.response?.data?.detail));
                }
            }
            setItems([]); setDiscount(0); setCashNoInvoice(false);
            setWaitHours(0); setTolls(0); setExtras([]);
            setEvento({ ...evento, lines: [] });
            setProspect({ name: "", phone: "", email: "", company: "", rfc: "" });
            setShowProspect(false);
            setDiscountReason("");
            setDiscountApproval(null);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const selectedClient = clients.find((c) => c.id === client);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="quoter-page">
            <div className="lg:col-span-2 space-y-4">
                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                        <Calculator className="w-4 h-4" /> Cotizador inteligente
                        <HelpTip text="Genera una cotización aplicando automáticamente la tarifa del convenio del cliente. Soporta descuentos por porcentaje o monto, y pagos en efectivo sin factura (sin IVA)." />
                    </h3>

                    {/* Selector de TIPO de cotización */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="quoter-kind-selector">
                        {[
                            { id: "traslado", label: "Traslado", help: "Servicio clínico de traslado: BLS, urgencia, intermedia, intensiva, foráneo, ida y vuelta." },
                            { id: "evento", label: "Evento", help: "Cobertura preventiva con N ambulancias y/o paramédicos pie tierra por horas. No suma traslado base." },
                            { id: "guardia", label: "Guardia / Renta", help: "Ambulancia fija en sitio por 12h, 24h o mensual. No suma traslado base." },
                            { id: "mixto", label: "Mixto", help: "Combina traslado + evento + guardia. Cuidado: es el que más se equivoca." },
                        ].map((k) => (
                            <button
                                type="button"
                                key={k.id}
                                onClick={() => setQuoteKind(k.id)}
                                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm border ${quoteKind === k.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                                title={k.help}
                                data-testid={`quoter-kind-${k.id}`}
                            >
                                {k.label}
                            </button>
                        ))}
                        <label className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none" title="Cuando está activo, muestra errores y advertencias que pueden bloquear la generación del PDF.">
                            <input type="checkbox" checked={auditMode} onChange={(e) => setAuditMode(e.target.checked)} className="w-3.5 h-3.5 accent-red-600" data-testid="quoter-audit-mode" />
                            Modo auditor
                        </label>
                    </div>
                    {quoteKind === "mixto" && (
                        <div className="mb-3 text-[11px] text-amber-800 bg-amber-50 border-l-4 border-l-amber-400 px-2.5 py-1.5 rounded-sm">
                            ⚠️ Cotización mixta: combinas conceptos de varios tipos. Revisa cuidadosamente cada línea.
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div>
                            <label className="fm-label flex items-center gap-1">Cliente <HelpTip text={FORM_HELP.client} /></label>
                            <select value={client} onChange={(e) => { setClient(e.target.value); if (e.target.value) setShowProspect(false); }} className="fm-input" data-testid="quoter-client" title={FORM_HELP.client}>
                                <option value="">— Sin cliente (prospecto) —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}{c.has_agreement ? " · ★" : ""}</option>)}
                            </select>
                            {selectedClient?.has_agreement && (
                                <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1"><Zap className="w-3 h-3" /> Cliente con convenio · se aplican tarifas especiales</div>
                            )}
                            {!client && (
                                <button
                                    type="button"
                                    onClick={() => setShowProspect((v) => !v)}
                                    className="text-[11px] text-indigo-700 mt-1 flex items-center gap-1 hover:underline"
                                    data-testid="quoter-prospect-toggle"
                                >
                                    <UserPlus className="w-3 h-3" /> {showProspect ? "Ocultar datos del prospecto" : "+ Capturar prospecto nuevo"}
                                </button>
                            )}
                        </div>
                        <div>
                            <label className="fm-label flex items-center gap-1">
                                Lista de precios
                                <HelpTip text="Catálogo tarifario aplicable. 'Público' es el default. Si el cliente tiene una lista asignada (por ejemplo Aseguradoras), se selecciona automáticamente." />
                            </label>
                            <select value={priceListId} onChange={(e) => setPriceListId(e.target.value)} className="fm-input" data-testid="quoter-price-list" title="Catálogo tarifario que se aplica a esta cotización.">
                                {priceLists.map((pl) => (
                                    <option key={pl.id} value={pl.id}>
                                        {pl.name}{pl.is_default ? " (público)" : ""}
                                    </option>
                                ))}
                            </select>
                            {selectedClient?.price_list_id && selectedClient.price_list_id === priceListId && (
                                <div className="text-[11px] text-indigo-700 mt-1 flex items-center gap-1" data-testid="quoter-auto-pricelist-banner">
                                    <Zap className="w-3 h-3" /> Lista del convenio aplicada automáticamente
                                </div>
                            )}
                            {selectedClient?.price_list_id && selectedClient.price_list_id !== priceListId && (
                                <button
                                    type="button"
                                    onClick={() => setPriceListId(selectedClient.price_list_id)}
                                    className="text-[11px] text-amber-700 mt-1 flex items-center gap-1 hover:underline"
                                    data-testid="quoter-restore-client-list"
                                >
                                    ⚠ Cambiaste la lista del convenio · click para restaurar
                                </button>
                            )}
                        </div>
                        <div>
                            <label className="fm-label flex items-center gap-1">Agregar concepto <HelpTip text={FORM_HELP.service_type} /></label>
                            <div className="flex gap-2">
                                <select onChange={(e) => { if (e.target.value) { setPreviewParams({ ...previewParams, service_type_code: e.target.value }); addItem(e.target.value); e.target.value = ""; } }} className="fm-input flex-1" defaultValue="" data-testid="quoter-add-concept" title="Selecciona el tipo de servicio para agregarlo a la cotización. Los marcados con (h) son por horas — captúralas abajo.">
                                    <option value="">Selecciona tipo de servicio...</option>
                                    {activePriceList?.custom_service_types?.length > 0 ? (() => {
                                        // FASE 2: Agrupar por categoría
                                        const grouped = {};
                                        const CATEGORY_LABELS = {
                                            servicio_base: "🚑 Servicios base",
                                            extra_clinico: "💉 Extras clínicos",
                                            operacion: "🛣️ Operación",
                                            evento: "🎫 Evento",
                                            renta_guardia: "🏠 Renta / Guardia",
                                            sin_categoria: "Otros",
                                        };
                                        (activePriceList.custom_service_types || []).forEach((s) => {
                                            const cat = s.category || "sin_categoria";
                                            (grouped[cat] = grouped[cat] || []).push(s);
                                        });
                                        const order = ["servicio_base", "extra_clinico", "operacion", "evento", "renta_guardia", "sin_categoria"];
                                        return order.filter((c) => grouped[c]).map((cat) => (
                                            <optgroup key={cat} label={`${CATEGORY_LABELS[cat]} · ${activePriceList.name}`}>
                                                {grouped[cat].map((s) => (
                                                    <option key={`pl-${s.code}`} value={s.code}>
                                                        {s.code} · {s.name} (${s.base_price})
                                                        {s.per_km ? ` · $${s.per_km}/km` : ""}
                                                        {s.hour_rate ? ` · $${s.hour_rate}/h` : ""}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ));
                                    })() : (
                                        <optgroup label="Catálogo público (FindMed)">
                                            {serviceTypes.map((s) => (
                                                <option key={s.code} value={s.code}>
                                                    {s.code} · {s.name}{s.per_hour ? " (por hora)" : ""} (${s.base_price})
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                                <button
                                    type="button"
                                    onClick={addManualItem}
                                    className="fm-btn-outline whitespace-nowrap text-xs"
                                    data-testid="quoter-add-manual"
                                    title="Agrega un concepto libre: tú escribes la descripción y el precio (ej. 'Hora de espera', 'Valoración sin traslado al 80%')."
                                >
                                    <Plus className="w-3.5 h-3.5" /> Manual
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ============ Form de Prospecto Nuevo (cuando NO hay cliente registrado) ============ */}
                    {!client && showProspect && (
                        <div className="border-l-4 border-l-indigo-500 bg-indigo-50/50 p-3 rounded-sm mb-3" data-testid="quoter-prospect-form">
                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-indigo-700 mb-2">
                                <UserPlus className="w-3.5 h-3.5" /> Datos del prospecto nuevo
                                <span className="ml-auto text-[10px] text-slate-500 normal-case font-normal">Se guarda en CRM al cotizar</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                                <input value={prospect.name} onChange={(e) => setProspect({ ...prospect, name: e.target.value })} className="fm-input text-xs" placeholder="Nombre completo" data-testid="prospect-name" />
                                <input value={prospect.phone} onChange={(e) => setProspect({ ...prospect, phone: e.target.value })} className="fm-input text-xs" placeholder="Teléfono / WhatsApp" data-testid="prospect-phone" />
                                <input value={prospect.email} onChange={(e) => setProspect({ ...prospect, email: e.target.value })} className="fm-input text-xs" placeholder="Email" data-testid="prospect-email" />
                                <input value={prospect.company} onChange={(e) => setProspect({ ...prospect, company: e.target.value })} className="fm-input text-xs" placeholder="Empresa (opcional)" data-testid="prospect-company" />
                                <input value={prospect.rfc} onChange={(e) => setProspect({ ...prospect, rfc: e.target.value })} className="fm-input text-xs uppercase" placeholder="RFC (opcional)" data-testid="prospect-rfc" maxLength={13} />
                            </div>
                        </div>
                    )}

                    {/* ============ Quick Chips · servicios y add-ons con un click ============ */}
                    {quoteKind === "traslado" && (
                        <div className="border-t border-slate-200 pt-3 mb-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-2 flex items-center gap-1.5">
                                ⚡ Servicios y extras · click para agregar
                                <HelpTip text="Botones rápidos para agregar los conceptos más comunes en un solo click. Cada chip muestra el precio del tarifario actual." />
                            </div>
                            <div className="space-y-2">
                                <div>
                                    <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">Atención prehospitalaria</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {QUICK_CHIPS.filter((c) => c.group === "base").map((c) => {
                                            const price = resolvePrice(c.code, c.fallback);
                                            const Icon = c.icon;
                                            return (
                                                <button
                                                    key={c.code}
                                                    type="button"
                                                    onClick={() => addQuickChip(c)}
                                                    className="group flex items-center gap-2 px-2.5 py-1.5 bg-white hover:bg-slate-900 hover:text-white border border-slate-200 hover:border-slate-900 rounded-sm transition-colors text-left"
                                                    data-testid={`quick-chip-${c.code}`}
                                                    title={`${c.sublabel} · agregar al carrito`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-white" strokeWidth={1.75} />
                                                    <div>
                                                        <div className="text-[11px] font-bold leading-tight">{c.label}</div>
                                                        <div className="text-[9px] font-mono tabular-nums text-slate-500 group-hover:text-slate-300">{money(price)}</div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">Extras clínicos (suma al servicio base)</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {QUICK_CHIPS.filter((c) => c.group === "extra").map((c) => {
                                            const price = resolvePrice(c.code, c.fallback);
                                            const Icon = c.icon;
                                            return (
                                                <button
                                                    key={c.code}
                                                    type="button"
                                                    onClick={() => addQuickChip(c)}
                                                    className="group flex items-center gap-2 px-2.5 py-1.5 bg-white hover:bg-indigo-600 hover:text-white border border-slate-200 hover:border-indigo-600 rounded-sm transition-colors"
                                                    data-testid={`quick-chip-${c.code}`}
                                                    title={`${c.sublabel} · agregar al carrito`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 text-indigo-500 group-hover:text-white" strokeWidth={1.75} />
                                                    <div>
                                                        <div className="text-[11px] font-bold leading-tight">{c.label}</div>
                                                        <div className="text-[9px] font-mono tabular-nums text-slate-500 group-hover:text-indigo-200">+ {money(price)}</div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                        {/* Ida y vuelta · multiplicador */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (items.length === 0) { toast.warning("Primero agrega el servicio base, después marca ida y vuelta."); return; }
                                                setTraslado({ ...traslado, is_round_trip: !traslado.is_round_trip, trips: !traslado.is_round_trip ? 2 : 1 });
                                                // Duplica el último item al activar
                                                if (!traslado.is_round_trip) {
                                                    const last = items[items.length - 1];
                                                    setItems([...items, { ...last, description: `${last.description} (regreso)`, total: last.unit_price * (last.qty || 1) }]);
                                                    toast.success("Ida y vuelta agregada");
                                                }
                                            }}
                                            className={`group flex items-center gap-2 px-2.5 py-1.5 border rounded-sm transition-colors ${traslado.is_round_trip ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600"}`}
                                            data-testid="quick-chip-roundtrip"
                                            title="Marca el traslado como ida y vuelta — duplica el cargo base."
                                        >
                                            <Repeat className={`w-3.5 h-3.5 ${traslado.is_round_trip ? "text-white" : "text-emerald-500 group-hover:text-white"}`} strokeWidth={1.75} />
                                            <div className="text-[11px] font-bold leading-tight">Ida y vuelta {traslado.is_round_trip ? "✓" : ""}</div>
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">Operación</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {QUICK_CHIPS.filter((c) => c.group === "ops").map((c) => {
                                            const price = resolvePrice(c.code, c.fallback);
                                            const Icon = c.icon;
                                            return (
                                                <button
                                                    key={c.code}
                                                    type="button"
                                                    onClick={() => addQuickChip(c)}
                                                    className="group flex items-center gap-2 px-2.5 py-1.5 bg-white hover:bg-slate-600 hover:text-white border border-slate-200 hover:border-slate-600 rounded-sm transition-colors"
                                                    data-testid={`quick-chip-${c.code}`}
                                                    title={`${c.sublabel} · agregar al carrito`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-white" strokeWidth={1.75} />
                                                    <div>
                                                        <div className="text-[11px] font-bold leading-tight">{c.label}</div>
                                                        <div className="text-[9px] font-mono tabular-nums text-slate-500 group-hover:text-slate-300">{money(price)}</div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            onClick={addManualItem}
                                            className="flex items-center gap-2 px-2.5 py-1.5 bg-amber-50 hover:bg-amber-600 hover:text-white border border-amber-300 hover:border-amber-600 rounded-sm transition-colors"
                                            data-testid="quick-chip-manual"
                                            title="Agrega un concepto libre con el precio que tú definas. Útil cuando el cliente pide algo no listado en tu tarifario."
                                        >
                                            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                                            <div className="text-[11px] font-bold leading-tight">Concepto manual</div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Atajos rápidos de conceptos del convenio (cancelaciones, % de base, etc.) */}
                    {(activePriceList?.custom_concepts?.length > 0) && (
                        <div className="border-t border-slate-200 pt-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
                                <span>Atajos del convenio · {activePriceList.name}</span>
                                <HelpTip text="Conceptos preconfigurados de la lista del cliente (cancelaciones, descuentos, cargos especiales). Da clic para agregarlos como línea automática." />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {activePriceList.custom_concepts.map((c, idx) => (
                                    <button
                                        key={c.id || idx}
                                        type="button"
                                        onClick={() => addCustomConcept(c)}
                                        className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 border border-slate-200 rounded-sm text-slate-700 inline-flex items-center gap-1"
                                        data-testid={`quoter-shortcut-${idx}`}
                                        title={`${c.label}${c.notes ? ` — ${c.notes}` : ""}`}
                                    >
                                        <Plus className="w-3 h-3" />
                                        <span className="truncate max-w-[260px]">{c.label}</span>
                                        <span className="text-slate-400 font-mono ml-1">
                                            {c.calculation === "percent_of_base" ? `${c.amount}%` : `$${c.amount}`}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* === FORMULARIOS CONDICIONALES POR TIPO DE COTIZACIÓN === */}
                    {(quoteKind === "traslado" || quoteKind === "mixto") && (
                        <div className="border-t border-slate-200 pt-4 mt-3">
                            <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-2">Datos del traslado</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                <div className="md:col-span-1">
                                    <label className="fm-label">Paciente</label>
                                    <input value={traslado.patient} onChange={(e) => setTraslado({ ...traslado, patient: e.target.value })} className="fm-input" placeholder="Nombre del paciente" data-testid="traslado-patient" />
                                </div>
                                <div>
                                    <label className="fm-label">Origen</label>
                                    <input value={traslado.origin} onChange={(e) => setTraslado({ ...traslado, origin: e.target.value })} className="fm-input" placeholder="Dirección o lugar" data-testid="traslado-origin" />
                                </div>
                                <div>
                                    <label className="fm-label">Destino</label>
                                    <input value={traslado.destination} onChange={(e) => setTraslado({ ...traslado, destination: e.target.value })} className="fm-input" placeholder="Hospital, dirección, etc." data-testid="traslado-destination" />
                                </div>
                                <div>
                                    <label className="fm-label">Fecha/Hora servicio</label>
                                    <input type="datetime-local" value={traslado.service_date} onChange={(e) => setTraslado({ ...traslado, service_date: e.target.value })} className="fm-input" data-testid="traslado-date" />
                                </div>
                                <div className="flex items-end gap-3">
                                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                        <input type="checkbox" checked={traslado.is_foreign} onChange={(e) => setTraslado({ ...traslado, is_foreign: e.target.checked })} className="w-3.5 h-3.5 accent-red-600" data-testid="traslado-foreign" />
                                        Foráneo
                                    </label>
                                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                        <input type="checkbox" checked={traslado.is_round_trip} onChange={(e) => setTraslado({ ...traslado, is_round_trip: e.target.checked })} className="w-3.5 h-3.5 accent-red-600" data-testid="traslado-round-trip" />
                                        Ida y vuelta
                                    </label>
                                </div>
                                {traslado.is_foreign && (
                                    <div>
                                        <label className="fm-label">Kilómetros foráneos</label>
                                        <input type="number" value={traslado.km} onChange={(e) => setTraslado({ ...traslado, km: parseFloat(e.target.value) || 0 })} className="fm-input" placeholder="0" data-testid="traslado-km" />
                                    </div>
                                )}
                                <div>
                                    <label className="fm-label">Tiempo de espera (min)</label>
                                    <input type="number" value={traslado.wait_minutes} onChange={(e) => setTraslado({ ...traslado, wait_minutes: parseInt(e.target.value) || 0 })} className="fm-input" placeholder="0" data-testid="traslado-wait" />
                                </div>
                            </div>
                        </div>
                    )}

                    {(quoteKind === "evento" || quoteKind === "mixto") && (
                        <div className="border-t border-slate-200 pt-4 mt-3">
                            <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-2">Datos del evento</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                <div>
                                    <label className="fm-label">Nombre del evento</label>
                                    <input value={evento.event_name} onChange={(e) => setEvento({ ...evento, event_name: e.target.value })} className="fm-input" placeholder="Carrera 10K Roma Norte" data-testid="evento-name" />
                                </div>
                                <div>
                                    <label className="fm-label">Lugar</label>
                                    <input value={evento.location} onChange={(e) => setEvento({ ...evento, location: e.target.value })} className="fm-input" placeholder="Parque México" data-testid="evento-location" />
                                </div>
                                <div>
                                    <label className="fm-label">Fecha</label>
                                    <input type="date" value={evento.date} onChange={(e) => setEvento({ ...evento, date: e.target.value })} className="fm-input" data-testid="evento-date" />
                                </div>
                                <div>
                                    <label className="fm-label">Inicio</label>
                                    <input type="time" value={evento.start_time} onChange={(e) => setEvento({ ...evento, start_time: e.target.value })} className="fm-input" data-testid="evento-start" />
                                </div>
                                <div>
                                    <label className="fm-label">Término</label>
                                    <input type="time" value={evento.end_time} onChange={(e) => setEvento({ ...evento, end_time: e.target.value })} className="fm-input" data-testid="evento-end" />
                                </div>
                                <div>
                                    <label className="fm-label">Responsable / Teléfono</label>
                                    <div className="flex gap-1">
                                        <input value={evento.responsible} onChange={(e) => setEvento({ ...evento, responsible: e.target.value })} className="fm-input flex-1" placeholder="Nombre" data-testid="evento-resp" />
                                        <input value={evento.responsible_phone} onChange={(e) => setEvento({ ...evento, responsible_phone: e.target.value })} className="fm-input flex-1" placeholder="Tel" data-testid="evento-tel" />
                                    </div>
                                </div>
                            </div>

                            {/* Líneas del evento (ambulancias y/o paramédicos pie tierra) */}
                            <div className="mt-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <h5 className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Líneas del evento</h5>
                                    <div className="flex gap-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const ambItem = mergedServiceTypes.find((s) => s.subtype === "ambulancia_evento_hora" || s.code === "EVT_AMB");
                                                const price = ambItem?.base_price || 900;
                                                setEvento({ ...evento, lines: [...evento.lines, { id: Date.now(), type: "ambulancia", qty: 1, hours: 4, unit_price: price, label: "Ambulancia con tripulación (evento)", notes: "" }] });
                                            }}
                                            className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-sm font-bold text-slate-700"
                                            data-testid="evento-add-amb"
                                        >
                                            + Ambulancia
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const pieItem = mergedServiceTypes.find((s) => s.subtype === "paramedico_pie_tierra_hora" || s.code === "EVT_PIE");
                                                const price = pieItem?.base_price || 250;
                                                setEvento({ ...evento, lines: [...evento.lines, { id: Date.now(), type: "paramedico", qty: 1, hours: 4, unit_price: price, label: "Paramédico pie tierra", notes: "" }] });
                                            }}
                                            className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-sm font-bold text-slate-700"
                                            data-testid="evento-add-para"
                                        >
                                            + Paramédico pie tierra
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const medItem = mergedServiceTypes.find((s) => s.code === "MED");
                                                const price = medItem?.base_price || 1200;
                                                setEvento({ ...evento, lines: [...evento.lines, { id: Date.now(), type: "medico", qty: 1, hours: 4, unit_price: price, label: "Médico responsiva", notes: "" }] });
                                            }}
                                            className="text-[11px] px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-sm font-bold"
                                            data-testid="evento-add-med"
                                        >
                                            + Médico
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const medEsp = mergedServiceTypes.find((s) => s.code === "MED_ESP");
                                                const price = medEsp?.base_price || 2500;
                                                setEvento({ ...evento, lines: [...evento.lines, { id: Date.now(), type: "medico_esp", qty: 1, hours: 4, unit_price: price, label: "Médico especialista", notes: "" }] });
                                            }}
                                            className="text-[11px] px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-sm font-bold"
                                            data-testid="evento-add-medesp"
                                        >
                                            + Médico especialista
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEvento({ ...evento, lines: [...evento.lines, { id: Date.now(), type: "manual", qty: 1, hours: 1, unit_price: 0, label: "Concepto personalizado", notes: "" }] });
                                            }}
                                            className="text-[11px] px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-sm font-bold"
                                            data-testid="evento-add-manual"
                                        >
                                            + Manual
                                        </button>
                                    </div>
                                </div>
                                {evento.lines.length === 0 ? (
                                    <div className="text-xs text-slate-400 italic py-3 text-center border border-dashed rounded-sm">Sin líneas. Agrega al menos una ambulancia o paramédico para cotizar el evento.</div>
                                ) : (
                                    <table className="w-full text-xs">
                                        <thead><tr className="border-b border-slate-200 bg-slate-50">
                                            <th className="text-left py-1.5 px-2 font-bold text-slate-500 uppercase tracking-wider text-[9px]">Concepto</th>
                                            <th className="text-right py-1.5 px-2 font-bold text-slate-500 uppercase tracking-wider text-[9px] w-16">Cant.</th>
                                            <th className="text-right py-1.5 px-2 font-bold text-slate-500 uppercase tracking-wider text-[9px] w-16">Horas</th>
                                            <th className="text-right py-1.5 px-2 font-bold text-slate-500 uppercase tracking-wider text-[9px] w-24">$/Hora</th>
                                            <th className="text-right py-1.5 px-2 font-bold text-slate-500 uppercase tracking-wider text-[9px] w-24">Total</th>
                                            <th className="w-8"></th>
                                        </tr></thead>
                                        <tbody>
                                            {evento.lines.map((l, idx) => {
                                                const lineTotal = (l.qty || 0) * (l.hours || 0) * (l.unit_price || 0);
                                                return (
                                                    <tr key={l.id} className="border-b border-slate-100">
                                                        <td className="py-1 px-2">
                                                            <input value={l.label} onChange={(e) => { const ls = [...evento.lines]; ls[idx] = { ...l, label: e.target.value }; setEvento({ ...evento, lines: ls }); }} className="fm-input text-xs" data-testid={`evento-line-label-${idx}`} />
                                                        </td>
                                                        <td className="py-1 px-2"><input type="number" min="0" value={l.qty} onChange={(e) => { const ls = [...evento.lines]; ls[idx] = { ...l, qty: parseFloat(e.target.value) || 0 }; setEvento({ ...evento, lines: ls }); }} className="fm-input text-xs text-right font-mono" data-testid={`evento-line-qty-${idx}`} /></td>
                                                        <td className="py-1 px-2"><input type="number" min="0" step="0.5" value={l.hours} onChange={(e) => { const ls = [...evento.lines]; ls[idx] = { ...l, hours: parseFloat(e.target.value) || 0 }; setEvento({ ...evento, lines: ls }); }} className="fm-input text-xs text-right font-mono" data-testid={`evento-line-hours-${idx}`} /></td>
                                                        <td className="py-1 px-2"><input type="number" min="0" value={l.unit_price} onChange={(e) => { const ls = [...evento.lines]; ls[idx] = { ...l, unit_price: parseFloat(e.target.value) || 0 }; setEvento({ ...evento, lines: ls }); }} className="fm-input text-xs text-right font-mono" data-testid={`evento-line-price-${idx}`} /></td>
                                                        <td className="py-1 px-2 text-right font-mono tabular-nums font-bold">{money(lineTotal)}</td>
                                                        <td className="py-1 px-2 text-center"><button type="button" onClick={() => setEvento({ ...evento, lines: evento.lines.filter((_, i) => i !== idx) })} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}

                    {(quoteKind === "guardia" || quoteKind === "mixto") && (
                        <div className="border-t border-slate-200 pt-4 mt-3">
                            <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-2">Datos de guardia / renta</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                <div>
                                    <label className="fm-label">Modalidad</label>
                                    <select value={guardia.modality} onChange={(e) => setGuardia({ ...guardia, modality: e.target.value })} className="fm-input" data-testid="guardia-modality">
                                        <option value="12h">Guardia 12 horas</option>
                                        <option value="24h">Guardia 24 horas</option>
                                        <option value="mensual_12h">Mensual 12 horas</option>
                                        <option value="mensual_24h">Mensual 24 horas</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="fm-label">No. de ambulancias</label>
                                    <input type="number" min="0" value={guardia.ambulances} onChange={(e) => setGuardia({ ...guardia, ambulances: parseInt(e.target.value) || 0 })} className="fm-input" data-testid="guardia-amb" />
                                </div>
                                <div>
                                    <label className="fm-label">Lugar</label>
                                    <input value={guardia.location} onChange={(e) => setGuardia({ ...guardia, location: e.target.value })} className="fm-input" placeholder="Domicilio o sitio" data-testid="guardia-location" />
                                </div>
                                <div>
                                    <label className="fm-label">Inicio</label>
                                    <input type="date" value={guardia.start_date} onChange={(e) => setGuardia({ ...guardia, start_date: e.target.value })} className="fm-input" data-testid="guardia-start" />
                                </div>
                                <div>
                                    <label className="fm-label">Término</label>
                                    <input type="date" value={guardia.end_date} onChange={(e) => setGuardia({ ...guardia, end_date: e.target.value })} className="fm-input" data-testid="guardia-end" />
                                </div>
                                <div>
                                    <label className="fm-label">Responsable</label>
                                    <input value={guardia.responsible} onChange={(e) => setGuardia({ ...guardia, responsible: e.target.value })} className="fm-input" data-testid="guardia-resp" />
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const mod = guardia.modality;
                                    const codeMap = { "12h": "G_12H", "24h": "G_24H", "mensual_12h": "G_M12H", "mensual_24h": "G_M24H" };
                                    const labelMap = { "12h": "Guardia 12h", "24h": "Guardia 24h", "mensual_12h": "Mensual 12h", "mensual_24h": "Mensual 24h" };
                                    // Fallback hard-coded del Tarifario Público oficial — para que NUNCA falle aunque la lista no se haya cargado.
                                    const fallbackPrices = { "G_12H": 10800, "G_24H": 21600, "G_M12H": 324000, "G_M24H": 648000 };
                                    const code = codeMap[mod];
                                    // 1) Buscar en lista activa, 2) En cualquier lista cargada, 3) Fallback hard-coded
                                    let price = 0;
                                    let it = mergedServiceTypes.find((s) => s.code === code || s.subtype === mod);
                                    if (it?.base_price) price = it.base_price;
                                    if (!price) {
                                        for (const pl of priceLists) {
                                            const m = (pl.custom_service_types || []).find((s) => s.code === code);
                                            if (m?.base_price) { price = m.base_price; break; }
                                        }
                                    }
                                    if (!price) price = fallbackPrices[code] || 0;
                                    if (!price) {
                                        toast.error(`No se encontró tarifa para ${labelMap[mod]}. Edita la tarifa manualmente.`);
                                        price = 0;
                                    }
                                    const total = price * (guardia.ambulances || 0);
                                    setItems([...items, {
                                        code,
                                        description: `${labelMap[mod]} × ${guardia.ambulances} ambulancia(s)`,
                                        qty: guardia.ambulances || 0,
                                        hours: 0,
                                        hourly_rate: 0,
                                        is_hourly: false,
                                        unit_price: price,
                                        total,
                                    }]);
                                    toast.success("Guardia agregada al carrito" + (price ? "" : " (precio en cero — edita manualmente)"));
                                }}
                                className="fm-btn-accent mt-2 text-xs"
                                data-testid="guardia-add-item"
                            >
                                <Plus className="w-3.5 h-3.5" /> Agregar guardia al carrito
                            </button>
                        </div>
                    )}

                    <div className="border-t border-slate-200 pt-4">
                        {items.length === 0 ? (
                            <div className="text-center py-8 text-sm text-slate-400">Agrega un concepto para comenzar</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead><tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left py-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Concepto</th>
                                    <th className="text-right py-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-20">Cant.</th>
                                    <th className="text-right py-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-28">P. Unit</th>
                                    <th className="text-right py-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-28">Total</th>
                                    <th className="w-8"></th>
                                </tr></thead>
                                <tbody>
                                    {items.map((it, i) => (
                                        <tr key={i} className="border-b border-slate-100">
                                            <td className="py-2 px-2">
                                                <div className="flex items-start gap-1">
                                                    <span className="font-mono text-xs text-slate-500 mt-0.5">{it.code}</span>
                                                    <div className="flex-1">
                                                        {it.is_manual ? (
                                                            <input
                                                                type="text"
                                                                value={it.description}
                                                                onChange={(e) => updateItem(i, "description", e.target.value)}
                                                                placeholder="Describe el concepto (ej. Hora de espera, Valoración sin traslado, etc.)"
                                                                className="fm-input text-xs w-full"
                                                                data-testid={`quote-item-desc-${i}`}
                                                            />
                                                        ) : (
                                                            <div>{it.description}{it.is_hourly && <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">POR HORA</span>}</div>
                                                        )}
                                                        {it.is_hourly && (
                                                            <div className="flex items-center gap-2 mt-1.5">
                                                                <label className="text-[10px] text-slate-500 font-mono">Horas:</label>
                                                                <input
                                                                    type="number"
                                                                    min="0.5"
                                                                    step="0.5"
                                                                    value={it.hours}
                                                                    onChange={(e) => updateItem(i, "hours", e.target.value)}
                                                                    className="fm-input w-16 text-right text-xs py-1"
                                                                    data-testid={`quote-item-hours-${i}`}
                                                                    title="Horas que dura el evento. Multiplica la tarifa por hora."
                                                                />
                                                                <span className="text-[10px] text-slate-500">× ${parseFloat(it.hourly_rate || 0).toLocaleString("es-MX")}/h</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-1 px-2"><input type="number" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} className="fm-input text-right" title="Cantidad de unidades de este concepto." /></td>
                                            <td className="py-1 px-2"><input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="fm-input text-right" disabled={it.is_hourly} title={it.is_hourly ? "Calculado automáticamente: base + (horas × tarifa/h)" : "Precio unitario manual."} /></td>
                                            <td className="py-2 px-2 text-right font-mono font-bold">{money(it.total)}</td>
                                            <td className="py-1 px-1"><button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700" title="Eliminar este concepto"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Cargos adicionales: espera, casetas, insumos extra */}
                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                        Cargos adicionales
                        <HelpTip text="Cobros que se suman al subtotal: tiempo de espera adicional, casetas/peajes que pagaste y se reembolsan, insumos clínicos extra (medicamentos, oxígeno, vendajes especiales), etc." />
                    </h3>

                    {/* Cancelación tardía / Salida fallida — sustituye el cobro normal */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                        <label className={`flex items-start gap-2 cursor-pointer p-3 border rounded-sm transition ${lateCancellation ? "border-red-400 bg-red-50" : "border-slate-200 hover:bg-slate-50"}`}>
                            <input
                                type="checkbox"
                                checked={lateCancellation}
                                onChange={(e) => { setLateCancellation(e.target.checked); if (e.target.checked) setFailedDeparture(false); }}
                                className="mt-0.5"
                                data-testid="quote-late-cancellation"
                            />
                            <div className="text-xs leading-snug flex-1">
                                <div className="font-bold text-slate-800 flex items-center gap-1">
                                    Cancelación tardía
                                    <HelpTip text="Cliente canceló después del corte: se cobra el 40% de la tarifa base (configurable por convenio: late_cancellation_pct). NO se cobran horas de espera, casetas, ni extras." />
                                </div>
                                <div className="text-slate-500">Cobro punitivo del 40% — no salió la unidad pero se canceló tarde.</div>
                            </div>
                        </label>
                        <label className={`flex items-start gap-2 cursor-pointer p-3 border rounded-sm transition ${failedDeparture ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:bg-slate-50"}`}>
                            <input
                                type="checkbox"
                                checked={failedDeparture}
                                onChange={(e) => { setFailedDeparture(e.target.checked); if (e.target.checked) setLateCancellation(false); }}
                                className="mt-0.5"
                                data-testid="quote-failed-departure"
                            />
                            <div className="text-xs leading-snug flex-1">
                                <div className="font-bold text-slate-800 flex items-center gap-1">
                                    Salida fallida
                                    <HelpTip text="La unidad SÍ salió pero el paciente no estaba o cancelaron al arribo: se cobra el 60% de la tarifa base (configurable por convenio: failed_departure_pct)." />
                                </div>
                                <div className="text-slate-500">Cobro del 60% — la unidad arribó pero no hubo servicio.</div>
                            </div>
                        </label>
                    </div>

                    {penaltyActive && (
                        <div className="mb-3 bg-red-50 border-l-4 border-l-red-500 px-3 py-2 text-xs text-red-900">
                            ⚠️ <strong>Modo cargo punitivo activo.</strong> Sólo se cobra la penalización ({lateCancellation ? "40%" : "60%"} de tarifa base). Los demás cargos quedan ignorados.
                        </div>
                    )}

                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 ${penaltyActive ? "opacity-40 pointer-events-none" : ""}`}>
                        <div>
                            <label className="fm-label flex items-center gap-1">
                                Horas de espera <HelpTip text="Tiempo que la ambulancia tuvo que esperar (paciente no listo, hospital tardó en recibir, etc). Se cobran a $350/h por default; configurable en el convenio del cliente." />
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={waitHours}
                                    onChange={(e) => setWaitHours(e.target.value)}
                                    className="fm-input text-right"
                                    placeholder="0"
                                    data-testid="quote-wait-hours"
                                    title="Horas de espera con paciente abordo o en sitio. Multiplica por la tarifa horaria del convenio."
                                />
                                <span className="text-[10px] text-slate-500 font-mono w-12">hrs</span>
                            </div>
                            {parseFloat(waitHours) > 0 && (
                                <div className="text-[10px] text-emerald-700 mt-1 font-mono">
                                    + {money(parseFloat(waitHours) * 350)} ({waitHours}h × $350/h)
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="fm-label flex items-center gap-1">
                                Casetas / Peajes <HelpTip text="Total de casetas y peajes que pagó la tripulación. Se reembolsa al cliente sin agregar IVA adicional. Pídele a la tripulación que conserve los recibos." />
                            </label>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-slate-400">$</span>
                                <input
                                    type="number"
                                    min="0"
                                    step="10"
                                    value={tolls}
                                    onChange={(e) => setTolls(e.target.value)}
                                    className="fm-input text-right"
                                    placeholder="0.00"
                                    data-testid="quote-tolls"
                                    title="Monto total de casetas y peajes. Se traslada al cliente."
                                />
                            </div>
                        </div>
                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={() => setExtras([...extras, { label: "", amount: 0 }])}
                                className="fm-btn-outline w-full justify-center"
                                data-testid="add-extra-btn"
                                title="Agrega un cargo libre: oxígeno extra, medicamentos, equipo especial, etc."
                            >
                                <Plus className="w-3.5 h-3.5" /> Agregar concepto extra
                            </button>
                        </div>
                    </div>

                    {extras.length > 0 && (
                        <div className="border-t border-slate-100 pt-3 space-y-2">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Conceptos extra (insumos, medicamentos, equipo)</div>
                            {extras.map((ex, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={ex.label}
                                        onChange={(e) => {
                                            const c = [...extras]; c[i].label = e.target.value; setExtras(c);
                                        }}
                                        placeholder="Ej. Oxígeno extra, Collarín cervical, Adrenalina..."
                                        className="fm-input flex-1 text-sm"
                                        data-testid={`extra-label-${i}`}
                                    />
                                    <span className="text-xs font-mono text-slate-400">$</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="10"
                                        value={ex.amount}
                                        onChange={(e) => {
                                            const c = [...extras]; c[i].amount = parseFloat(e.target.value) || 0; setExtras(c);
                                        }}
                                        className="fm-input w-32 text-right text-sm"
                                        placeholder="0.00"
                                        data-testid={`extra-amount-${i}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setExtras(extras.filter((_, idx) => idx !== i))}
                                        className="text-red-500 hover:text-red-700"
                                        title="Quitar este concepto"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Live price preview */}
                {preview && (
                    <div className="fm-card p-5 border-l-4 border-l-emerald-500">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display font-bold text-base flex items-center gap-2"><Zap className="w-4 h-4 text-emerald-600" /> Preview en vivo · Motor de precios</h3>
                            <div className="flex items-center gap-3 text-[10px]">
                                <label className="flex items-center gap-1 cursor-pointer">
                                    <input type="checkbox" checked={previewParams.is_foreign} onChange={(e) => setPreviewParams({ ...previewParams, is_foreign: e.target.checked })} /> Foráneo
                                </label>
                                {previewParams.is_foreign && (
                                    <input type="number" placeholder="km" value={previewParams.km} onChange={(e) => setPreviewParams({ ...previewParams, km: parseFloat(e.target.value) || 0 })} className="fm-input w-20 text-xs py-1" />
                                )}
                            </div>
                        </div>
                        <div className="space-y-1 text-xs">
                            {preview.breakdown.map(([label, amount], i) => (
                                <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                                    <span className="text-slate-600">{label}</span>
                                    <span className={`font-mono tabular-nums ${amount < 0 ? "text-red-600" : "text-slate-900"}`}>{money(amount)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between font-bold pt-2 mt-2 border-t border-slate-300">
                                <span>Total con IVA</span>
                                <span className="font-mono tabular-nums">{money(preview.total)}</span>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200 text-[11px]">
                                <span className="text-slate-500">Costo estimado: <span className="font-mono">{money(preview.estimated_cost)}</span></span>
                                <span className={`font-bold ${preview.margin_pct > 50 ? "text-emerald-700" : preview.margin_pct > 30 ? "text-amber-700" : "text-red-700"}`}>
                                    Margen estimado: {money(preview.margin)} ({preview.margin_pct.toFixed(1)}%)
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                {preview.is_night && <span className="fm-badge fm-badge-warning"><Moon className="w-3 h-3 inline mr-1" />Tarifa nocturna</span>}
                                {preview.is_holiday && <span className="fm-badge fm-badge-warning"><Calendar className="w-3 h-3 inline mr-1" />Día festivo</span>}
                                {previewParams.is_foreign && <span className="fm-badge fm-badge-neutral"><MapPin className="w-3 h-3 inline mr-1" />Foráneo</span>}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="fm-card p-5 sticky top-4 self-start">
                <h3 className="font-display font-bold text-base mb-4">Resumen</h3>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Conceptos</span><span className="font-mono tabular-nums">{money(subtotalItems)}</span></div>
                    {waitCharge > 0 && (
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-500">+ Espera ({waitHours}h)</span>
                            <span className="font-mono tabular-nums">{money(waitCharge)}</span>
                        </div>
                    )}
                    {tollsAmount > 0 && (
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-500">+ Casetas</span>
                            <span className="font-mono tabular-nums">{money(tollsAmount)}</span>
                        </div>
                    )}
                    {extrasTotal > 0 && (
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-500">+ Extras ({extras.filter(e => parseFloat(e.amount) > 0).length})</span>
                            <span className="font-mono tabular-nums">{money(extrasTotal)}</span>
                        </div>
                    )}
                    <div className="flex justify-between border-t border-slate-100 pt-2 font-bold">
                        <span className="text-slate-700">Subtotal</span>
                        <span className="font-mono tabular-nums" data-testid="quote-subtotal">{money(subtotal)}</span>
                    </div>

                    {/* Descuento: por % o por monto fijo */}
                    <div className="pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <span className="text-slate-500 text-xs flex items-center gap-1">
                                Descuento
                                <HelpTip text="Aplica un descuento sobre el subtotal antes de IVA. Cambia entre porcentaje (%) y monto fijo (MXN) según la promoción o lo que pacte el cliente." />
                            </span>
                            <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden text-[10px] font-bold uppercase tracking-wider">
                                <button
                                    type="button"
                                    onClick={() => setDiscountMode("pct")}
                                    className={`px-2 py-1 transition ${discountMode === "pct" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`}
                                    data-testid="quote-discount-mode-pct"
                                >%</button>
                                <button
                                    type="button"
                                    onClick={() => setDiscountMode("amount")}
                                    className={`px-2 py-1 transition ${discountMode === "amount" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`}
                                    data-testid="quote-discount-mode-amount"
                                >MXN</button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min="0"
                                step={discountMode === "pct" ? "1" : "10"}
                                value={discount}
                                onChange={(e) => setDiscount(e.target.value)}
                                className="fm-input flex-1 text-right text-xs"
                                placeholder={discountMode === "pct" ? "0" : "0.00"}
                                data-testid="quote-discount-input"
                            />
                            <span className="text-xs font-mono text-slate-500 w-8">{discountMode === "pct" ? "%" : "MXN"}</span>
                        </div>
                        <div className="flex justify-between mt-1.5 text-xs">
                            <span className="text-slate-500">Descuento aplicado</span>
                            <span className="font-mono tabular-nums text-red-600" data-testid="quote-discount-amount">-{money(discountAmt)}</span>
                        </div>
                    </div>

                    {/* Sin IVA */}
                    <div className="pt-2 border-t border-slate-100">
                        <label className="flex items-start gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={cashNoInvoice}
                                onChange={(e) => setCashNoInvoice(e.target.checked)}
                                className="mt-0.5"
                                data-testid="quote-cash-no-invoice"
                            />
                            <div className="text-xs leading-snug">
                                <div className="font-bold text-slate-800 flex items-center gap-1">
                                    Pago en efectivo sin factura
                                    <HelpTip text={FORM_HELP.cash_no_invoice} />
                                </div>
                                <div className="text-slate-500">No se cobra IVA. El total se acepta sólo como ingreso en efectivo, sin CFDI.</div>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-between pt-2 border-t border-slate-100">
                        <span className="text-slate-500">Subtotal gravable</span>
                        <span className="font-mono tabular-nums">{money(taxable)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">IVA 16%</span>
                        <span className="font-mono tabular-nums" data-testid="quote-iva">
                            {cashNoInvoice ? <span className="text-amber-600">EXENTO</span> : money(iva)}
                        </span>
                    </div>
                    <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                        <span className="font-display font-bold text-base">TOTAL</span>
                        <span className="font-mono font-black text-2xl tabular-nums" data-testid="quote-total">{money(total)}</span>
                    </div>
                    {cashNoInvoice && (
                        <div className="text-[10px] font-mono text-amber-700 bg-amber-50 border-l-2 border-amber-400 px-2 py-1 mt-1">
                            ⚠️ EFECTIVO · SIN CFDI · No se podrá deducir fiscalmente
                        </div>
                    )}
                </div>

                {/* Motivo del descuento (cuando hay descuento) */}
                {discountAmt > 0 && (
                    <div className="mt-3">
                        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Motivo del descuento {discountMode === "pct" && (parseFloat(discount) || 0) > 20 && <span className="text-red-600">*</span>}</label>
                        <input
                            value={discountReason}
                            onChange={(e) => setDiscountReason(e.target.value)}
                            className="fm-input text-xs"
                            placeholder="Ej. Cliente recurrente, autorizó Dirección, campaña promocional..."
                            data-testid="quoter-discount-reason"
                        />
                    </div>
                )}

                {/* Panel del Modo Auditor */}
                {auditMode && (auditErrors.length > 0 || auditWarnings.length > 0) && (                    <div className="mt-3 border-2 border-dashed rounded-sm p-2.5 text-[11px] space-y-1" data-testid="quoter-audit-panel">
                        {auditErrors.map((e, i) => (
                            <div key={`err-${i}`} className="text-red-700 flex items-start gap-1.5"><span className="font-bold">✕</span><span>{e}</span></div>
                        ))}
                        {auditWarnings.map((w, i) => (
                            <div key={`warn-${i}`} className="text-amber-700 flex items-start gap-1.5"><span className="font-bold">⚠</span><span>{w}</span></div>
                        ))}
                        {/* Botón de autorización rápida cuando descuento excede el máximo */}
                        {discountAmt > 0 && discountPct > discountMaxPct && !discountApproval && (
                            <button
                                type="button"
                                onClick={() => {
                                    const reason = prompt(
                                        `Autorizar descuento de ${discountPct.toFixed(1)}% (supera el ${discountMaxPct}% configurado en el tarifario).\n\nMotivo de la autorización:`,
                                        discountReason || ""
                                    );
                                    if (!reason) return;
                                    setDiscountApproval({
                                        by_name: "Autorización local",
                                        at: new Date().toISOString(),
                                        reason,
                                    });
                                    setDiscountReason(reason);
                                    toast.success("Descuento autorizado. La autorización quedará registrada al guardar.");
                                }}
                                className="mt-2 text-[11px] px-2.5 py-1 bg-amber-100 hover:bg-amber-200 border border-amber-400 rounded-sm font-bold text-amber-900"
                                data-testid="quoter-authorize-discount"
                            >
                                🛡️ Autorizar descuento especial
                            </button>
                        )}
                        {discountApproval && (
                            <div className="text-emerald-700 flex items-start gap-1.5 mt-1"><span className="font-bold">✓</span><span>Descuento autorizado · {discountApproval.reason}</span></div>
                        )}
                    </div>
                )}

                {/* === FASE 3: Comparador Público vs Cliente === */}
                {compareEnabled && items.length > 0 && (
                    <div className="mt-3 border border-slate-200 rounded-sm overflow-hidden" data-testid="quoter-compare-panel">
                        <button
                            type="button"
                            onClick={() => setShowCompare(!showCompare)}
                            className="w-full px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left flex items-center justify-between text-xs font-bold"
                            data-testid="quoter-compare-toggle"
                        >
                            <span className="flex items-center gap-2">
                                <Calculator className="w-3.5 h-3.5 text-indigo-600" />
                                Comparar con Tarifario Público
                            </span>
                            <span className={`font-mono tabular-nums ${compareDelta < 0 ? "text-red-600" : "text-emerald-600"}`}>
                                {compareDelta >= 0 ? "+" : ""}{money(compareDelta)} ({compareDeltaPct >= 0 ? "+" : ""}{compareDeltaPct.toFixed(1)}%)
                            </span>
                        </button>
                        {showCompare && (
                            <div className="p-3 bg-white text-[11px] space-y-2">
                                <div className="text-[10px] text-slate-500 leading-snug">
                                    Diferencia entre lo que cobraría el <b>Tarifario Público</b> vs lo que aplica el tarifario del cliente <b>{activePriceList?.name}</b>. Útil para sustentar al cliente el valor del convenio.
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[11px]">
                                        <thead>
                                            <tr className="border-b border-slate-200 text-[9px] uppercase tracking-wider text-slate-500">
                                                <th className="text-left py-1 pr-2">Concepto</th>
                                                <th className="text-right py-1 px-1">Público</th>
                                                <th className="text-right py-1 px-1">{activePriceList?.name}</th>
                                                <th className="text-right py-1 pl-1">Δ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {compareRows.map((r) => (
                                                <tr key={r.idx} className="border-b border-slate-100">
                                                    <td className="py-1 pr-2">
                                                        <div className="font-mono text-[10px] text-slate-500">{r.code}</div>
                                                        <div className="truncate max-w-[180px]">{r.description}</div>
                                                        {!r.has_match && <div className="text-[9px] text-amber-700">sin equivalente en público</div>}
                                                    </td>
                                                    <td className="text-right font-mono tabular-nums py-1 px-1 text-slate-600">{money(r.public_total)}</td>
                                                    <td className="text-right font-mono tabular-nums py-1 px-1 font-bold">{money(r.client_total)}</td>
                                                    <td className={`text-right font-mono tabular-nums py-1 pl-1 ${r.client_total - r.public_total < 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                        {(r.client_total - r.public_total) >= 0 ? "+" : ""}{money(r.client_total - r.public_total)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="border-t-2 border-slate-300 font-bold">
                                                <td className="py-1.5 pr-2">Total con IVA</td>
                                                <td className="text-right font-mono tabular-nums py-1.5 px-1 text-slate-700">{money(compareTotalPublic)}</td>
                                                <td className="text-right font-mono tabular-nums py-1.5 px-1">{money(compareTotalClient)}</td>
                                                <td className={`text-right font-mono tabular-nums py-1.5 pl-1 ${compareDelta < 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                    {compareDelta >= 0 ? "+" : ""}{money(compareDelta)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="text-[10px] text-slate-500 leading-snug pt-1 border-t border-slate-100">
                                    {compareDelta < 0
                                        ? `🎯 El convenio le ahorra ${money(-compareDelta)} al cliente (${(-compareDeltaPct).toFixed(1)}% menos que el público).`
                                        : compareDelta > 0
                                        ? `⚠️ El convenio cobra ${money(compareDelta)} más que el público. Verifica con Dirección antes de enviar.`
                                        : "Tarifarios equivalentes en este carrito."}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <button
                    onClick={() => save()}
                    disabled={(items.length === 0 && eventoLinesTotal === 0) || auditBlocked}
                    className="fm-btn-accent w-full justify-center mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-quote-btn"
                    title={auditBlocked ? "Modo auditor activo: resuelve los errores rojos para guardar" : ""}
                >
                    <Save className="w-3.5 h-3.5" /> {auditBlocked ? "Guardar (bloqueado por auditor)" : "Guardar cotización"}
                </button>                <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                        onClick={() => save(["whatsapp"])}
                        disabled={(items.length === 0 && eventoLinesTotal === 0) || auditBlocked}
                        className="fm-btn-outline justify-center text-xs bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="save-and-send-wa-btn"
                        title="Guarda y manda la cotización por WhatsApp al teléfono del cliente/prospecto"
                    >
                        <Save className="w-3.5 h-3.5" /> Guardar + WhatsApp
                    </button>
                    <button
                        onClick={() => save(["email"])}
                        disabled={(items.length === 0 && eventoLinesTotal === 0) || auditBlocked}
                        className="fm-btn-outline justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="save-and-send-email-btn"
                        title="Guarda y manda la cotización por correo al email del cliente/prospecto"
                    >
                        <Save className="w-3.5 h-3.5" /> Guardar + Email
                    </button>
                </div>
            </div>
        </div>
    );
}
